import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getKPITeamList } from "services/data.kpi.service";
import { deactivateKPITeam } from "services/data.kpi.service";
import { ActiveButton } from "ui-components/IconButton";
import { activateKPITeam } from "services/data.kpi.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getKPITeamStatusCount } from "services/data.kpi.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function KPITeamList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["kpi-team-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["KPI Team"]) &&
      permissions["KPI Team"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["KPI Team"]) &&
      permissions["KPI Team"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["KPI Team"]) &&
      permissions["KPI Team"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["kpi-team-list", { customerCode, active }],
    queryFn: () => getKPITeamList({ active }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["kpi-team-list", "status", { customerCode }],
    queryFn: () => getKPITeamStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateKPITeam, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activateKPITeam, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/kpi/kpi-team/add");

  const onEdit = (req) => {
    console.log(req);
    navigate("/kpi/kpi-team/edit", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.team}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.team}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Team", accessorKey: "team" },
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "User Range Name", accessorKey: "user" },
    { header: "Productivity Range Name", accessorKey: "productivity" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { id,team } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit kpi team"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ id,team });
                }}
              />
              <DelButton
                title="deactivate kpi team"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ id,team });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate kpi team"
              disabled={!editPermission}
              onClick={() => onActive({ id,team })}
            />
          );
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;KPI&nbsp;Team&nbsp;
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default KPITeamList;
